import React, { createContext, useContext, useState, useEffect } from 'react';

const ProxySessionContext = createContext();

export const ProxySessionProvider = ({ children }) => {
  const [proxySession, setProxySession] = useState(null);
  const [isProxyMode, setIsProxyMode] = useState(false);

  // Load proxy session from localStorage on mount
  useEffect(() => {
    const savedProxySession = localStorage.getItem('proxySession');
    if (savedProxySession) {
      try {
        const parsedSession = JSON.parse(savedProxySession);
        setProxySession(parsedSession);
        setIsProxyMode(true);
      } catch (error) {
        console.error('Error parsing saved proxy session:', error);
        localStorage.removeItem('proxySession');
      }
    }
  }, []);

  // Start proxy session
  const startProxySession = (proxyData) => {
    const proxySessionData = {
      adminUser: proxyData.adminUser,
      proxyUser: proxyData.proxyUser,
      proxySessionId: proxyData.proxySessionId,
      expiresAt: proxyData.expiresAt,
      startedAt: new Date().toISOString(),
    };

    // Save to localStorage
    localStorage.setItem('proxySession', JSON.stringify(proxySessionData));
    
    // Update token and user data for proxy user
    localStorage.setItem('token', proxyData.token);
    localStorage.setItem('role', proxyData.proxyUser.role);
    localStorage.setItem('user', JSON.stringify(proxyData.proxyUser));
    
    // Update state
    setProxySession(proxySessionData);
    setIsProxyMode(true);

    console.log('Proxy session started:', proxySessionData);
  };

  // End proxy session (called after API handles token refresh)
  const endProxySession = () => {
    if (!proxySession) return;

    // Remove proxy session data
    localStorage.removeItem('proxySession');
    
    // Clear proxy state
    setProxySession(null);
    setIsProxyMode(false);

    console.log('Proxy session ended');
  };

  // Get current user (proxy user if in proxy mode, otherwise actual user)
  const getCurrentUser = () => {
    if (isProxyMode && proxySession) {
      return proxySession.proxyUser;
    }
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  };

  // Get admin user (only available in proxy mode)
  const getAdminUser = () => {
    return isProxyMode && proxySession ? proxySession.adminUser : null;
  };

  // Check if proxy session is expired
  const isProxySessionExpired = () => {
    if (!proxySession || !proxySession.expiresAt) return false;
    return new Date() > new Date(proxySession.expiresAt);
  };

  // Auto-cleanup expired sessions
  useEffect(() => {
    if (isProxyMode && isProxySessionExpired()) {
      console.warn('Proxy session expired, automatically ending session');
      endProxySession();
    }
  }, [isProxyMode, proxySession]);

  const value = {
    proxySession,
    isProxyMode,
    startProxySession,
    endProxySession,
    getCurrentUser,
    getAdminUser,
    isProxySessionExpired,
  };

  return (
    <ProxySessionContext.Provider value={value}>
      {children}
    </ProxySessionContext.Provider>
  );
};

export const useProxySession = () => {
  const context = useContext(ProxySessionContext);
  if (!context) {
    throw new Error('useProxySession must be used within a ProxySessionProvider');
  }
  return context;
};

export default ProxySessionContext;